"use client";

import * as React from "react";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import { cn } from "@workspace/ui/lib/utils";
import { SquarePlay } from "lucide-react";
import Link from "next/link";

type BrowserTabsProps = React.ComponentProps<typeof TabsPrimitive.Root>;

function BrowserTabs({ className, ...props }: BrowserTabsProps) {
  return (
    <TabsPrimitive.Root
      data-slot="browser-tabs"
      className={cn(
        "flex flex-col gap-[18px] rounded-[24px] bg-[#f3e4c9] p-2.5 pt-[18px] w-full max-w-[1120px]",
        className,
      )}
      {...props}
    />
  );
}

type BrowserTabsListProps = React.ComponentProps<typeof TabsPrimitive.List>;

function BrowserTabsList({ className, ...props }: BrowserTabsListProps) {
  return (
    <div className="flex items-center pl-[22px] gap-6 w-full">
      <div className="flex shrink-0 items-center gap-3">
        <span className="size-3 lg:size-4 bg-[#ed5931] rounded-full" />
        <span className="size-3 lg:size-4 bg-[#FFAD62] rounded-full" />
        <span className="size-3 lg:size-4 bg-[#C9B829] rounded-full" />
      </div>
      <TabsPrimitive.List
        data-slot="browser-tabs-list"
        className={cn(
          "flex items-center gap-2 overflow-x-auto overflow-y-hidden",
          className,
        )}
        {...props}
      />
    </div>
  );
}

/** Pixels per second the title travels while sliding. Reading pace, not a ticker. */
const MARQUEE_SPEED = 45;
/**
 * How long the title rests at each end, in ms. Fixed rather than a share of the
 * cycle: a title that only just overflows would otherwise twitch back and forth
 * on a pause too short to read it in.
 */
const MARQUEE_HOLD = 2200;

/**
 * A title too long for its pill slides back and forth instead of being cut off
 * mid-word. A tab stretched to the full frame — which is every tab on a phone
 * once there is only one of them — leaves no row left to scroll sideways, so
 * without this the tail of the title is simply unreachable.
 */
function BrowserTabLabel({ children }: { children?: React.ReactNode }) {
  const viewportRef = React.useRef<HTMLSpanElement>(null);
  const textRef = React.useRef<HTMLSpanElement>(null);
  const [hiddenWidth, setHiddenWidth] = React.useState(0);

  React.useEffect(() => {
    const viewport = viewportRef.current;
    const text = textRef.current;
    if (!viewport || !text) return;

    const measure = () => {
      const hidden = text.scrollWidth - viewport.clientWidth;
      // A stray pixel or two is sub-pixel rounding, not something to animate.
      setHiddenWidth(hidden > 2 ? hidden : 0);
    };

    measure();
    // The webfont swapping in re-measures the title, and so does a rotated phone.
    const observer = new ResizeObserver(measure);
    observer.observe(viewport);
    observer.observe(text);
    return () => observer.disconnect();
  }, [children]);

  // Scripted rather than a CSS animation: the distance is only known once the
  // title has been measured, and keyframe offsets cannot be driven by a custom
  // property — which is what it would take to hold for a fixed time at each end
  // however far the title has to travel.
  React.useEffect(() => {
    const text = textRef.current;
    if (!text || hiddenWidth <= 0) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const pass = (hiddenWidth / MARQUEE_SPEED) * 1000;
    const cycle = (pass + MARQUEE_HOLD) * 2;
    const at = (elapsed: number) => elapsed / cycle;
    const away = `translateX(${-hiddenWidth}px)`;

    // Each keyframe's easing governs the stretch that follows it, so the passes
    // ease and the holds stay put.
    const animation = text.animate(
      [
        { transform: "translateX(0)", offset: 0, easing: "linear" },
        {
          transform: "translateX(0)",
          offset: at(MARQUEE_HOLD),
          easing: "ease-in-out",
        },
        { transform: away, offset: at(MARQUEE_HOLD + pass), easing: "linear" },
        {
          transform: away,
          offset: at(MARQUEE_HOLD * 2 + pass),
          easing: "ease-in-out",
        },
        { transform: "translateX(0)", offset: 1 },
      ],
      { duration: cycle, iterations: Infinity },
    );

    return () => animation.cancel();
  }, [hiddenWidth]);

  return (
    <span
      ref={viewportRef}
      className="min-w-0 flex-1 overflow-hidden"
      data-slot="browser-tab-label"
    >
      <span
        ref={textRef}
        className="block w-max whitespace-nowrap font-semibold text-black"
      >
        {children}
      </span>
    </span>
  );
}

type BrowserTabProps = React.ComponentProps<typeof TabsPrimitive.Trigger> & {
  icon?: React.ReactNode;
  href?: string;
};

function BrowserTab({
  className,
  children,
  icon,
  href,
  ...props
}: BrowserTabProps) {
  const tabClassName = cn(
    "flex items-center h-11 bg-white rounded-[10px] gap-2.5 px-3 cursor-pointer",
    "min-w-0 max-w-full shrink-0",
    "data-[state=inactive]:bg-white/50 data-[state=inactive]:opacity-70",
    "transition-all duration-200",
    className,
  );

  const content = (
    <>
      {icon || (
        <SquarePlay className="shrink-0 [&>rect]:fill-d-main [&>rect]:stroke-d-main [&>path]:fill-black" />
      )}
      <BrowserTabLabel>{children}</BrowserTabLabel>
    </>
  );

  if (href) {
    return (
      <TabsPrimitive.Trigger
        data-slot="browser-tab"
        className={tabClassName}
        asChild
        {...props}
      >
        <Link href={href} scroll={false}>
          {content}
        </Link>
      </TabsPrimitive.Trigger>
    );
  }

  return (
    <TabsPrimitive.Trigger
      data-slot="browser-tab"
      className={tabClassName}
      {...props}
    >
      {content}
    </TabsPrimitive.Trigger>
  );
}

type BrowserTabContentProps = React.ComponentProps<
  typeof TabsPrimitive.Content
>;

function BrowserTabContent({ className, ...props }: BrowserTabContentProps) {
  return (
    <TabsPrimitive.Content
      data-slot="browser-tab-content"
      className={cn(
        "relative flex aspect-video w-full bg-white rounded-[20px] border border-[#e3b8c4] overflow-hidden",
        className,
      )}
      {...props}
    />
  );
}

export { BrowserTabs, BrowserTabsList, BrowserTab, BrowserTabContent };
